import { useMergedRefs } from '@base-ui/utils/useMergedRefs';
import { ChevronDownIcon } from 'lucide-react';
import {
    useContext,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ComponentPropsWithRef,
    type CSSProperties,
    type ReactElement,
} from 'react';
import { cn } from '../util/classes.ts';
import { useControllableState } from '../util/use-controllable-state.tsx';
import { ExpandableContext, type ExpandableContextValue } from './expandable-context.ts';

function useExpandable(part: string): ExpandableContextValue {
    const context = useContext(ExpandableContext);
    if (context === undefined) {
        throw new Error(`${part} must be rendered inside an Expandable.`);
    }
    return context;
}

/** Resolves a `px` or `rem` length, the units the `--expandable-*` lengths are set in. */
function toPx(length: string): number {
    const value = Number.parseFloat(length);
    if (length.trimEnd().endsWith('rem')) {
        return value * Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
    }
    return value;
}

export type ExpandableProps = ComponentPropsWithRef<'div'> & {
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
};

/**
 * Long read-only content shown as its first few lines, fading out, with a toggle to read the
 * rest in place. Content that fits shows in full and the toggle stays hidden.
 *
 * Sets `data-overflowing` while the content is taller than its collapsed height plus the fold
 * slack, `data-open`
 * while expanded and `data-collapsed` while overflowing and closed, so surrounding markup can
 * style itself with `group-data-*` against `group/expandable`.
 */
export function Expandable({
    className,
    open: openProp,
    defaultOpen = false,
    onOpenChange,
    ...props
}: ExpandableProps): ReactElement {
    const contentId = useId();
    const [open, setOpen] = useControllableState({
        value: openProp,
        defaultValue: defaultOpen,
        onChange: onOpenChange,
    });
    const [overflowing, setOverflowing] = useState(false);

    const context = useMemo<ExpandableContextValue>(
        () => ({ contentId, open, setOpen, setOverflowing }),
        [contentId, open, setOpen],
    );

    return (
        <ExpandableContext value={context}>
            <div
                data-slot="expandable"
                data-open={open ? '' : undefined}
                data-overflowing={overflowing ? '' : undefined}
                data-collapsed={overflowing && !open ? '' : undefined}
                className={cn('group/expandable flex min-w-0 flex-col items-start', className)}
                {...props}
            />
        </ExpandableContext>
    );
}

export type ExpandableContentProps = ComponentPropsWithRef<'div'>;

/**
 * The content itself. Collapsed, it is cut to `--expandable-collapsed-height` (default
 * 8.75rem, about four lines of body text) with the bottom `--expandable-fade-height` fading
 * out. Content that is taller by no more than `--expandable-fold-slack` (default 2.5rem, two
 * lines) shows in full instead, so the toggle never opens just a line or two. Override any of
 * them with a class such as `[--expandable-collapsed-height:6rem]`, in `px` or `rem`. Opening
 * animates to the content's full height.
 */
export function ExpandableContent({ className, style, ref, ...props }: ExpandableContentProps): ReactElement {
    const { contentId, setOverflowing } = useExpandable('ExpandableContent');
    const contentRef = useRef<HTMLDivElement>(null);
    const mergedRef = useMergedRefs(ref, contentRef);
    const [fullHeight, setFullHeight] = useState<number>();

    useLayoutEffect(() => {
        const element = contentRef.current;
        if (!element) {
            return;
        }

        // The box itself is clamped, so watch its children for the height the content wants.
        const measure = (): void => {
            const computed = getComputedStyle(element);
            const collapsedHeight = toPx(computed.getPropertyValue('--expandable-collapsed-height'));
            const foldSlack = toPx(computed.getPropertyValue('--expandable-fold-slack'));
            setFullHeight(element.scrollHeight);
            setOverflowing(element.scrollHeight > collapsedHeight + foldSlack + 1);
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        for (const child of element.children) {
            observer.observe(child);
        }

        return () => {
            observer.disconnect();
        };
    }, [setOverflowing]);

    return (
        <div
            ref={mergedRef}
            id={contentId}
            data-slot="expandable-content"
            style={
                {
                    ...(fullHeight === undefined ? {} : { '--expandable-full-height': `${fullHeight}px` }),
                    ...style,
                } as CSSProperties
            }
            className={cn(
                '[--expandable-collapsed-height:8.75rem] [--expandable-fade-height:4rem] [--expandable-fold-slack:2.5rem]',
                'w-full min-w-0 overflow-hidden',
                'max-h-[var(--expandable-full-height,none)]',
                'transition-[max-height] duration-interactive ease-standard',
                'group-data-collapsed/expandable:max-h-(--expandable-collapsed-height)',
                'group-data-collapsed/expandable:[mask-image:linear-gradient(to_bottom,black_calc(100%-var(--expandable-fade-height)),transparent)]',
                className,
            )}
            {...props}
        />
    );
}

export type ExpandableToggleProps = Omit<ComponentPropsWithRef<'button'>, 'children'> & {
    /** Label while collapsed, e.g. "Show more". */
    expandLabel: string;
    /** Label while expanded, e.g. "Show less". */
    collapseLabel: string;
};

/** Opens and closes the content. Hidden while the content fits. */
export function ExpandableToggle({
    expandLabel,
    collapseLabel,
    className,
    onClick,
    ...props
}: ExpandableToggleProps): ReactElement {
    const { contentId, open, setOpen } = useExpandable('ExpandableToggle');

    return (
        <button
            type="button"
            data-slot="expandable-toggle"
            aria-expanded={open}
            aria-controls={contentId}
            onClick={(event) => {
                onClick?.(event);
                if (!event.defaultPrevented) {
                    setOpen(!open);
                }
            }}
            className={cn(
                'hidden group-data-overflowing/expandable:inline-flex',
                'min-h-interactive cursor-pointer items-center gap-tight select-none',
                'text-ui-base text-content-weak hover:text-content',
                'transition-colors duration-state ease-state',
                className,
            )}
            {...props}
        >
            {open ? collapseLabel : expandLabel}
            <ChevronDownIcon
                aria-hidden
                className="size-4! transition-transform duration-state ease-state group-data-open/expandable:rotate-180"
            />
        </button>
    );
}
